import { useState, type FormEvent } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, HardHat, Plus, FileText, NotebookPen, CheckCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

// ============================================================================
// Detalle de proyecto
// ============================================================================
export function ObraDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [openCtr, setOpenCtr] = useState(false);
  const [openBit, setOpenBit] = useState(false);

  const { data: p } = useQuery<any>({ queryKey: ['obra', id], queryFn: () => api(`/obra/proyectos/${id}`) });

  if (!p) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  const avance = p.contratos?.length > 0
    ? p.contratos.reduce((a: number, c: any) => a + (c.monto_ejecutado ?? 0), 0) /
      Math.max(1, p.contratos.reduce((a: number, c: any) => a + (c.monto_contratado ?? 0), 0)) * 100
    : 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/obras" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a proyectos
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <HardHat size={20} />
            </div>
            <div>
              <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700">{p.folio} · {p.fuente_financiamiento}</p>
              <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">{p.nombre}</h1>
              <p className="text-[12px] text-ink-mute capitalize">{p.tipo.replace(/_/g, ' ')} · {p.estado.replace(/_/g, ' ')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setOpenBit(true)}><NotebookPen size={14} /> Nota bitácora</Button>
            <Button onClick={() => setOpenCtr(true)}><Plus size={14} /> Contratar obra</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat label="Autorizado" value={fmt.money(p.presupuesto_autorizado)} />
        <Stat label="Contratado" value={fmt.money(p.contratos?.reduce((a: number, c: any) => a + c.monto_contratado, 0) ?? 0)} />
        <Stat label="Ejecutado" value={fmt.money(p.contratos?.reduce((a: number, c: any) => a + c.monto_ejecutado, 0) ?? 0)} sub={`${avance.toFixed(1)}%`} />
        <Stat label="Beneficiarios" value={(p.beneficiarios ?? '—').toString()} />
      </div>

      {p.descripcion && (
        <Card>
          <CardHeader title="Descripción" />
          <CardBody><p className="text-[13px]">{p.descripcion}</p></CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Contratos de obra" subtitle={`${(p.contratos ?? []).length} contratos`} />
        <CardBody className="p-0">
          {(p.contratos ?? []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-ink-mute">Sin contratos de obra todavía.</div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Contrato / Nº</th>
                  <th className="px-5 py-3">Contratista</th>
                  <th className="px-5 py-3">Modalidad</th>
                  <th className="px-5 py-3 text-right">Monto</th>
                  <th className="px-5 py-3 text-right">Ejecutado</th>
                  <th className="px-5 py-3 text-right">Avance</th>
                  <th className="px-5 py-3">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {p.contratos.map((c: any) => (
                  <tr key={c.id} className="hover:bg-muted/50">
                    <td className="px-5 py-3">
                      <div className="num text-[12px] font-semibold">{c.contrato_folio}</div>
                      <div className="num text-[11px] text-ink-mute">{c.numero_contrato}</div>
                    </td>
                    <td className="px-5 py-3">{c.proveedor_nombre}</td>
                    <td className="px-5 py-3 text-[12px] capitalize">{c.modalidad_obra?.replace(/_/g, ' ')}</td>
                    <td className="num px-5 py-3 text-right font-semibold">{fmt.money(c.monto_contratado)}</td>
                    <td className="num px-5 py-3 text-right">{fmt.money(c.monto_ejecutado)}</td>
                    <td className="num px-5 py-3 text-right">{(c.avance_financiero_pct ?? 0).toFixed(1)}%</td>
                    <td className="px-5 py-3"><EstadoCtrPill e={c.estado} /></td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/app/obras/${id}/contratos/${c.id}`}><Button variant="ghost" size="sm">Ver</Button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Bitácora de obra" />
        <CardBody className="p-0">
          {(p.bitacora ?? []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-ink-mute">Sin notas todavía.</div>
          ) : (
            <ul className="divide-y divide-border">
              {p.bitacora.map((b: any) => (
                <li key={b.id} className="px-5 py-3">
                  <div className="flex items-center gap-2 text-[11px] text-ink-mute">
                    <span className="rounded bg-muted px-1.5 py-0.5 uppercase">{b.tipo}</span>
                    <span>{fmt.date(b.fecha)}</span>
                  </div>
                  <p className="mt-1 text-[13px]">{b.descripcion}</p>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <ContratarObraModal proyectoID={id} open={openCtr} onClose={() => setOpenCtr(false)}
        onDone={(coID: string) => { setOpenCtr(false); qc.invalidateQueries({ queryKey: ['obra', id] }); if (coID) navigate(`/app/obras/${id}/contratos/${coID}`); }} />
      <BitacoraModal proyectoID={id} open={openBit} onClose={() => setOpenBit(false)}
        onDone={() => { setOpenBit(false); qc.invalidateQueries({ queryKey: ['obra', id] }); }} />
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-2 text-[18px] font-semibold text-ink">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-ink-mute">{sub}</div>}
    </div>
  );
}

function EstadoCtrPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    vigente: 'bg-emerald-50 text-emerald-700',
    suspendido: 'bg-amber-50 text-amber-700',
    finiquitado: 'bg-brand-50 text-brand-700',
    rescindido: 'bg-rose-50 text-rose-700',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', cfg[e] ?? 'bg-muted')}>{e}</span>;
}

function BitacoraModal({ proyectoID, open, onClose, onDone }: any) {
  const [tipo, setTipo] = useState('nota');
  const [descripcion, setDescripcion] = useState('');
  const [subm, setSubm] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setSubm(true);
    try {
      await api(`/obra/proyectos/${proyectoID}/bitacora`, { method: 'POST', body: { tipo, descripcion } });
      setDescripcion(''); onDone();
    } catch (x) { alert(x instanceof ApiError ? x.message : 'Error'); }
    finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Agregar nota a bitácora"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="bit-form" loading={subm}>Guardar</Button>
      </>}>
      <form id="bit-form" onSubmit={submit} className="flex flex-col gap-3">
        <div>
          <label className="text-[13px] font-medium">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="nota">Nota</option>
            <option value="incidencia">Incidencia</option>
            <option value="instruccion">Instrucción</option>
            <option value="cambio">Cambio</option>
            <option value="suspension">Suspensión</option>
            <option value="reanudacion">Reanudación</option>
            <option value="entrega_parcial">Entrega parcial</option>
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium">Descripción</label>
          <textarea required rows={4} value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px]" />
        </div>
      </form>
    </Modal>
  );
}

type Concepto = { codigo: string; descripcion: string; unidad: string; cantidad_contratada: number; precio_unitario: number };

function ContratarObraModal({ proyectoID, open, onClose, onDone }: any) {
  const [contratoID, setContratoID] = useState('');
  const [modalidad, setModalidad] = useState('precios_unitarios');
  const [plazoDias, setPlazoDias] = useState(0);
  const [fIni, setFIni] = useState('');
  const [fFin, setFFin] = useState('');
  const [monto, setMonto] = useState(0);
  const [anticipo, setAnticipo] = useState(0);
  const [conceptos, setConceptos] = useState<Concepto[]>([
    { codigo: '', descripcion: '', unidad: '', cantidad_contratada: 0, precio_unitario: 0 },
  ]);
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: contratos } = useQuery({
    queryKey: ['contratos-obra-disponibles'], enabled: open,
    queryFn: () => api<any[]>('/contratos?estado=firmado'),
  });
  const { data: contratosVig } = useQuery({
    queryKey: ['contratos-vigentes-disponibles'], enabled: open,
    queryFn: () => api<any[]>('/contratos?estado=vigente'),
  });

  const opciones = [...(contratos ?? []), ...(contratosVig ?? [])].filter(
    c => c.tipo === 'obra_publica' || c.tipo === 'servicio_relacionado_obra'
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      const res = await api<{ id: string }>('/obra/contratos', {
        method: 'POST',
        body: {
          proyecto_id: proyectoID,
          contrato_id: contratoID,
          modalidad_obra: modalidad,
          plazo_dias: Number(plazoDias),
          fecha_inicio: new Date(fIni).toISOString(),
          fecha_fin_programada: new Date(fFin).toISOString(),
          monto_contratado: Number(monto),
          monto_anticipo: Number(anticipo),
          conceptos: conceptos.filter(c => c.codigo && c.cantidad_contratada > 0),
        },
      });
      onDone(res.id);
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Contratar obra" size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="co-form" loading={subm}><FileText size={14} /> Registrar contrato</Button>
      </>}>
      <form id="co-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}

        <div>
          <label className="text-[13px] font-medium">Contrato marco (obra pública, firmado o vigente)</label>
          <select required value={contratoID} onChange={(e) => setContratoID(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="">(selecciona)</option>
            {opciones.map(c => (
              <option key={c.id} value={c.id}>{c.folio} — {c.numero_contrato} · {c.proveedor_nombre}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[13px] font-medium">Modalidad</label>
            <select value={modalidad} onChange={(e) => setModalidad(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
              <option value="precios_unitarios">Precios unitarios</option>
              <option value="precio_alzado">Precio alzado</option>
              <option value="mixto">Mixto</option>
            </select>
          </div>
          <Input name="plazo" label="Plazo (días)" type="number" required
            value={plazoDias} onChange={(e) => setPlazoDias(parseInt(e.target.value) || 0)} />
          <Input name="ini" label="Inicio" type="date" required value={fIni} onChange={(e) => setFIni(e.target.value)} />
          <Input name="fin" label="Fin programado" type="date" required value={fFin} onChange={(e) => setFFin(e.target.value)} />
          <Input name="monto" label="Monto contratado" type="number" step="0.01" required
            value={monto} onChange={(e) => setMonto(parseFloat(e.target.value) || 0)} />
          <Input name="ant" label="Monto anticipo" type="number" step="0.01"
            value={anticipo} onChange={(e) => setAnticipo(parseFloat(e.target.value) || 0)} />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Conceptos / Catálogo</h4>
            <Button type="button" size="sm" variant="ghost"
              onClick={() => setConceptos([...conceptos, { codigo: '', descripcion: '', unidad: '', cantidad_contratada: 0, precio_unitario: 0 }])}>
              + concepto
            </Button>
          </div>
          <div className="mt-2 overflow-hidden rounded-md border border-border">
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[10px] uppercase text-ink-mute">
                <tr>
                  <th className="px-3 py-2 w-20">Código</th>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2 w-16">Unidad</th>
                  <th className="px-3 py-2 text-right w-24">Cantidad</th>
                  <th className="px-3 py-2 text-right w-28">P.U.</th>
                  <th className="px-3 py-2 text-right w-28">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {conceptos.map((c, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">
                      <input type="text" value={c.codigo}
                        onChange={(e) => setConceptos(conceptos.map((x, j) => j === i ? { ...x, codigo: e.target.value } : x))}
                        className="num h-8 w-full rounded border border-border bg-surface px-2" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={c.descripcion}
                        onChange={(e) => setConceptos(conceptos.map((x, j) => j === i ? { ...x, descripcion: e.target.value } : x))}
                        className="h-8 w-full rounded border border-border bg-surface px-2" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="text" value={c.unidad}
                        onChange={(e) => setConceptos(conceptos.map((x, j) => j === i ? { ...x, unidad: e.target.value } : x))}
                        className="h-8 w-full rounded border border-border bg-surface px-2 text-[11px]" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.001" value={c.cantidad_contratada}
                        onChange={(e) => setConceptos(conceptos.map((x, j) => j === i ? { ...x, cantidad_contratada: parseFloat(e.target.value) || 0 } : x))}
                        className="num h-8 w-full rounded border border-border bg-surface px-2 text-right" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.0001" value={c.precio_unitario}
                        onChange={(e) => setConceptos(conceptos.map((x, j) => j === i ? { ...x, precio_unitario: parseFloat(e.target.value) || 0 } : x))}
                        className="num h-8 w-full rounded border border-border bg-surface px-2 text-right" />
                    </td>
                    <td className="num px-3 py-2 text-right font-semibold">{fmt.money(c.cantidad_contratada * c.precio_unitario)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================================
// Detalle de contrato de obra (con estimaciones)
// ============================================================================
export function ContratoObraDetallePage() {
  const { id = '', coId = '' } = useParams();
  const qc = useQueryClient();
  const [openEst, setOpenEst] = useState(false);
  const [openFin, setOpenFin] = useState(false);

  const { data: co } = useQuery<any>({ queryKey: ['contrato-obra', coId], queryFn: () => api(`/obra/contratos/${coId}`) });

  async function autorizar(estID: string) {
    try {
      await api(`/obra/estimaciones/${estID}/autorizar`, { method: 'POST', body: {} });
      qc.invalidateQueries({ queryKey: ['contrato-obra', coId] });
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  if (!co) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  const avance = co.monto_contratado > 0 ? (co.monto_ejecutado * 100 / co.monto_contratado) : 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to={`/app/obras/${id}`} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver al proyecto
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[22px] font-bold">Contrato de obra</h1>
            <p className="text-[12px] text-ink-mute capitalize">
              {co.modalidad_obra?.replace(/_/g, ' ')} · {co.estado} · plazo {co.plazo_dias} días
            </p>
          </div>
          <div className="flex gap-2">
            {co.estado === 'vigente' && (
              <>
                <Button onClick={() => setOpenEst(true)}><Plus size={14} /> Nueva estimación</Button>
                <Button variant="secondary" onClick={() => setOpenFin(true)}><CheckCheck size={14} /> Finiquitar</Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat label="Contratado" value={fmt.money(co.monto_contratado)} />
        <Stat label="Ejecutado" value={fmt.money(co.monto_ejecutado)} sub={`${avance.toFixed(1)}%`} />
        <Stat label="Pagado" value={fmt.money(co.monto_pagado)} />
        <Stat label="Anticipo" value={fmt.money(co.monto_anticipo)} sub={`amort ${fmt.money(co.anticipo_amortizado)}`} />
      </div>

      <Card>
        <CardHeader title="Catálogo de conceptos" />
        <CardBody className="p-0">
          <table className="min-w-full text-[13px]">
            <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
              <tr>
                <th className="px-5 py-3">Código</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3">Unidad</th>
                <th className="px-5 py-3 text-right">Contratado</th>
                <th className="px-5 py-3 text-right">Ejecutado</th>
                <th className="px-5 py-3 text-right">Pendiente</th>
                <th className="px-5 py-3 text-right">P.U.</th>
                <th className="px-5 py-3 text-right">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(co.conceptos ?? []).map((c: any) => (
                <tr key={c.id}>
                  <td className="num px-5 py-3">{c.codigo}</td>
                  <td className="px-5 py-3">{c.descripcion}</td>
                  <td className="px-5 py-3 text-[11px]">{c.unidad}</td>
                  <td className="num px-5 py-3 text-right">{fmt.num(c.cantidad_contratada)}</td>
                  <td className="num px-5 py-3 text-right text-emerald-700">{fmt.num(c.cantidad_ejecutada)}</td>
                  <td className="num px-5 py-3 text-right">{fmt.num(c.cantidad_pendiente)}</td>
                  <td className="num px-5 py-3 text-right">{fmt.money(c.precio_unitario)}</td>
                  <td className="num px-5 py-3 text-right font-semibold">{fmt.money(c.monto_contratado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Estimaciones" subtitle={`${(co.estimaciones ?? []).length} estimaciones`} />
        <CardBody className="p-0">
          {(co.estimaciones ?? []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-ink-mute">Sin estimaciones.</div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">#</th>
                  <th className="px-5 py-3">Periodo</th>
                  <th className="px-5 py-3 text-right">Subtotal</th>
                  <th className="px-5 py-3 text-right">Amort. ant.</th>
                  <th className="px-5 py-3 text-right">Ret. SFP (5‰)</th>
                  <th className="px-5 py-3 text-right">Ret. CMIC (2‰)</th>
                  <th className="px-5 py-3 text-right">IVA</th>
                  <th className="px-5 py-3 text-right">Líquido</th>
                  <th className="px-5 py-3">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {co.estimaciones.map((e: any) => (
                  <tr key={e.id}>
                    <td className="num px-5 py-3 font-semibold">#{e.numero}</td>
                    <td className="num px-5 py-3 text-[11px] text-ink-mute">{fmt.date(e.periodo_inicio)} → {fmt.date(e.periodo_fin)}</td>
                    <td className="num px-5 py-3 text-right">{fmt.money(e.subtotal)}</td>
                    <td className="num px-5 py-3 text-right text-rose-700">-{fmt.money(e.amortizacion_anticipo)}</td>
                    <td className="num px-5 py-3 text-right text-rose-700">-{fmt.money(e.retencion_sfp)}</td>
                    <td className="num px-5 py-3 text-right text-rose-700">-{fmt.money(e.retencion_cmic)}</td>
                    <td className="num px-5 py-3 text-right">{fmt.money(e.iva)}</td>
                    <td className="num px-5 py-3 text-right font-semibold">{fmt.money(e.total_liquido)}</td>
                    <td className="px-5 py-3 text-[11px] capitalize">{e.estado}</td>
                    <td className="px-5 py-3 text-right">
                      {e.estado === 'presentada' && (
                        <Button size="sm" variant="secondary" onClick={() => autorizar(e.id)}>Autorizar</Button>
                      )}
                      {e.cxp_id && <span className="text-[10px] text-emerald-700">CxP ✓</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <EstimacionModal coID={coId} conceptos={co.conceptos ?? []} open={openEst}
        onClose={() => setOpenEst(false)}
        onDone={() => { setOpenEst(false); qc.invalidateQueries({ queryKey: ['contrato-obra', coId] }); }} />
      <FiniquitoModal coID={coId} monto={co.monto_ejecutado} open={openFin}
        onClose={() => setOpenFin(false)}
        onDone={() => { setOpenFin(false); qc.invalidateQueries({ queryKey: ['contrato-obra', coId] }); }} />
    </div>
  );
}

function EstimacionModal({ coID, conceptos, open, onClose, onDone }: any) {
  const [pIni, setPIni] = useState('');
  const [pFin, setPFin] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, number>>({});
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const lineas = Object.entries(cantidades)
    .filter(([_, q]) => q > 0)
    .map(([concepto_id, cantidad]) => ({ concepto_id, cantidad }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (lineas.length === 0) { setErr('Captura al menos una cantidad'); return; }
    setSubm(true); setErr(null);
    try {
      await api(`/obra/contratos/${coID}/estimaciones`, {
        method: 'POST',
        body: {
          periodo_inicio: new Date(pIni).toISOString(),
          periodo_fin: new Date(pFin).toISOString(),
          lineas,
        },
      });
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nueva estimación" size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="est-form" loading={subm}>Crear estimación</Button>
      </>}>
      <form id="est-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Input name="pi" label="Periodo inicio" type="date" required value={pIni} onChange={(e) => setPIni(e.target.value)} />
          <Input name="pf" label="Periodo fin" type="date" required value={pFin} onChange={(e) => setPFin(e.target.value)} />
        </div>

        <div>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Volúmenes ejecutados este periodo</h4>
          <div className="overflow-hidden rounded-md border border-border">
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[10px] uppercase text-ink-mute">
                <tr>
                  <th className="px-3 py-2">Cód</th>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2 text-right">Pendiente</th>
                  <th className="px-3 py-2 text-right">A ejecutar</th>
                  <th className="px-3 py-2 text-right">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {conceptos.filter((c: any) => c.cantidad_pendiente > 0).map((c: any) => {
                  const q = cantidades[c.id] ?? 0;
                  return (
                    <tr key={c.id}>
                      <td className="num px-3 py-2">{c.codigo}</td>
                      <td className="px-3 py-2">
                        <div>{c.descripcion}</div>
                        <div className="text-[11px] text-ink-mute">{c.unidad} · {fmt.money(c.precio_unitario)}</div>
                      </td>
                      <td className="num px-3 py-2 text-right">{fmt.num(c.cantidad_pendiente)}</td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" step="0.001" min="0" max={c.cantidad_pendiente * 1.1} value={q}
                          onChange={(e) => setCantidades({ ...cantidades, [c.id]: parseFloat(e.target.value) || 0 })}
                          className="num h-8 w-24 rounded border border-border bg-surface px-2 text-right" />
                      </td>
                      <td className="num px-3 py-2 text-right font-semibold">{fmt.money(q * c.precio_unitario)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function FiniquitoModal({ coID, monto, open, onClose, onDone }: any) {
  const [acta, setActa] = useState('');
  const [saldo, setSaldo] = useState(0);
  const [ajuste, setAjuste] = useState(0);
  const [firmadoPor, setFirmadoPor] = useState('');
  const [subm, setSubm] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setSubm(true);
    try {
      await api(`/obra/contratos/${coID}/finiquitar`, {
        method: 'POST',
        body: {
          acta_numero: acta || undefined,
          monto_ejecutado_final: monto,
          saldo_pendiente: saldo,
          ajuste_final: ajuste,
          firmado_por: firmadoPor || undefined,
        },
      });
      onDone();
    } catch (x) { alert(x instanceof ApiError ? x.message : 'Error'); }
    finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Finiquitar contrato de obra"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="fin-form" variant="danger" loading={subm}>Finiquitar</Button>
      </>}>
      <form id="fin-form" onSubmit={submit} className="grid grid-cols-2 gap-3">
        <div className="col-span-2 rounded-md bg-amber-50 p-3 text-[12px] text-amber-900">
          Monto ejecutado final: <strong>{fmt.money(monto)}</strong>. Al finiquitar, el contrato pasa a estado "finiquitado"
          y el proyecto también si es su único contrato.
        </div>
        <Input name="acta" label="Nº acta" value={acta} onChange={(e) => setActa(e.target.value)} />
        <Input name="firm" label="Firmado por" value={firmadoPor} onChange={(e) => setFirmadoPor(e.target.value)} />
        <Input name="saldo" label="Saldo pendiente" type="number" step="0.01" value={saldo}
          onChange={(e) => setSaldo(parseFloat(e.target.value) || 0)} />
        <Input name="aj" label="Ajuste final" type="number" step="0.01" value={ajuste}
          onChange={(e) => setAjuste(parseFloat(e.target.value) || 0)} />
      </form>
    </Modal>
  );
}
